import {
  RA_ADDITIONAL_STATUSES,
  RA_ASSESSMENT_TYPES,
  RA_CONTROL_HIERARCHY,
  RA_CONTROL_REQUIREMENTS,
  RA_EXPECTED_EFFECTS,
  RA_IN_PLACE,
  RA_LIBRARY_MAX_CHECKS,
  RA_VESSEL_GROUPS,
  type RaAdditionalControl,
  type RaExistingControl,
  type RaLibraryCheck,
  type RaLibraryContent,
  type RaLibraryRow,
  type RaMatrixDefinition,
  type RaPublishIssue,
  raRowRatings,
} from '@felix/domain';
import {
  ActionIcon,
  Button,
  Checkbox,
  MultiSelect,
  Select,
  Tabs,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconCopy, IconLink, IconPlus, IconQuestionMark, IconTrash } from '@tabler/icons-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../../EmptyState.js';
import { DayInput } from '../../../inputs/DayInput.js';
import { StatusBadge } from '../../../StatusBadge.js';
import { LevelBadge } from '../RiskParts.js';
import classes from './RaLibrary.module.css';
import {
  blankAdditional,
  blankCheck,
  blankExisting,
  blankRow,
  duplicateRow,
  issueRow,
  textOrNull,
} from './view.js';

export const BUILDER_TABS = [
  'general',
  'rows',
  'initial',
  'existing',
  'additional',
  'residual',
  'publication',
] as const;
export type BuilderTab = (typeof BUILDER_TABS)[number];

const SCALE = ['1', '2', '3', '4', '5'];

export interface RaBuilderProps {
  content: RaLibraryContent;
  matrix: RaMatrixDefinition;
  editable: boolean;
  onChange?: (next: RaLibraryContent) => void;
  /** "Link existing hazard" seçenekleri (ofis; ana verideki etkin tehlikeler). */
  hazards?: readonly { id: string; code: string; name: string }[];
  /** Değerlendirmenin kimliği ve revizyonu (Genel sekmesinde salt okunur). */
  code: string;
  revision: string;
  tab: BuilderTab;
  onTab: (tab: BuilderTab) => void;
  /** Sekme 7'nin içeriği: iş akışı, sürümler, atama. */
  publication: ReactNode;
  issues?: readonly RaPublishIssue[];
}

/**
 * Yedi sekmeli Builder (K3; FD §5, R8–R14): General, satırlar (adım + tehlike + sonuç), Initial
 * Risk, Existing Controls, Additional Controls, Residual ve yayım. Skor elle girilmez; L ve S
 * seçilir, seviye ve kabul durumu matristen türer. Salt okunurken (yayımlı sürüm, incelemedeki
 * revizyon, gemi) aynı düzen alanlarsız gösterilir.
 */
export function RaBuilder(props: RaBuilderProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskLibrary.${key}`, options);
  const issueCount = (tab: BuilderTab) =>
    (props.issues ?? []).filter((i) => tabOfIssue(i.code) === tab).length;
  return (
    <>
      <Tabs
        className={classes.tabs}
        value={props.tab}
        onChange={(value) => value && props.onTab(value as BuilderTab)}
      >
        <Tabs.List>
          {BUILDER_TABS.map((tab) => {
            const count = props.editable ? issueCount(tab) : 0;
            return (
              <Tabs.Tab key={tab} value={tab}>
                {k(`tabs.${tab}`)}
                {count > 0 ? <span className={classes.muted}> ({count})</span> : null}
              </Tabs.Tab>
            );
          })}
        </Tabs.List>
      </Tabs>
      <div className={classes.body}>
        {props.tab === 'general' ? <GeneralTab {...props} /> : null}
        {props.tab === 'rows' ? <RowsTab {...props} /> : null}
        {props.tab === 'initial' ? <InitialTab {...props} /> : null}
        {props.tab === 'existing' ? <ExistingTab {...props} /> : null}
        {props.tab === 'additional' ? <AdditionalTab {...props} /> : null}
        {props.tab === 'residual' ? <ResidualTab {...props} /> : null}
        {props.tab === 'publication' ? props.publication : null}
      </div>
    </>
  );
}

/** Sorunun sekmesi (`view.ts` `issueTab` ile aynı). */
function tabOfIssue(code: RaPublishIssue['code']): BuilderTab {
  if (code.startsWith('REVIEW') || code.startsWith('PREPARED')) return 'general';
  if (code === 'ROWS_REQUIRED' || code === 'HAZARD_REQUESTED') return 'rows';
  if (code.startsWith('CURRENT')) return 'existing';
  if (code.startsWith('ADDITIONAL')) return 'additional';
  return 'residual';
}

function useLabels() {
  const { t } = useTranslation();
  return {
    t,
    k: (key: string, options: Record<string, unknown> = {}) => t(`riskLibrary.${key}`, options),
    hierarchy: RA_CONTROL_HIERARCHY.map((h) => ({
      value: h,
      label: t(`riskMaster.hierarchy.${h}`),
    })),
  };
}

/** Salt okunur alan: etiket ve değer. */
function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div className={classes.muted} style={{ fontSize: '0.75rem' }}>
        {label}
      </div>
      <div style={{ overflowWrap: 'anywhere' }}>{value ?? '—'}</div>
    </div>
  );
}

function setRow(
  props: RaBuilderProps,
  index: number,
  patch: Partial<RaLibraryRow> | ((row: RaLibraryRow) => RaLibraryRow),
) {
  props.onChange?.({
    ...props.content,
    rows: props.content.rows.map((row, i) =>
      i === index ? (typeof patch === 'function' ? patch(row) : { ...row, ...patch }) : row,
    ),
  });
}

function rowTitle(
  k: (key: string, o?: Record<string, unknown>) => string,
  row: RaLibraryRow,
  n: number,
) {
  return k('rows.rowTitle', { n, step: row.step || '—', hazard: row.hazard || '—' });
}

// ---------------------------------------------------------------------------------------------
// Sekme 1: General

function GeneralTab(props: RaBuilderProps) {
  const { t, k } = useLabels();
  const g = props.content.general;
  const set = (patch: Partial<RaLibraryContent['general']>) =>
    props.onChange?.({ ...props.content, general: { ...g, ...patch } });
  const ro = !props.editable;
  const activity = g.activityCode
    ? `${g.activityCode} · ${g.activityName ?? ''}`
    : k('general.noActivity');
  const text = (key: 'location' | 'team', max: number) =>
    ro ? (
      <Field label={k(`general.${key}`)} value={g[key]} />
    ) : (
      <TextInput
        label={k(`general.${key}`)}
        value={g[key] ?? ''}
        maxLength={max}
        onChange={(e) => set({ [key]: textOrNull(e.currentTarget.value) })}
      />
    );
  const area = (key: 'procedures' | 'scope' | 'assumptions' | 'exclusions', max: number) =>
    ro ? (
      <Field label={k(`general.${key}`)} value={g[key]} />
    ) : (
      <Textarea
        label={k(`general.${key}`)}
        value={g[key] ?? ''}
        maxLength={max}
        autosize
        minRows={2}
        onChange={(e) => set({ [key]: textOrNull(e.currentTarget.value) })}
      />
    );
  return (
    <div className={classes.grid}>
      <Field label={k('general.id')} value={props.code} />
      <Field label={k('general.revision')} value={props.revision} />
      <Field label={k('general.activity')} value={activity} />
      {ro ? (
        <Field label={k('general.title')} value={g.title} />
      ) : (
        <TextInput
          className={classes.span2}
          label={k('general.title')}
          required
          value={g.title}
          maxLength={200}
          onChange={(e) => set({ title: e.currentTarget.value })}
        />
      )}
      {ro ? (
        <Field
          label={k('general.type')}
          value={t(`riskMaster.assessmentTypes.${g.assessmentType}`)}
        />
      ) : (
        <Select
          label={k('general.type')}
          allowDeselect={false}
          value={g.assessmentType}
          data={RA_ASSESSMENT_TYPES.map((v) => ({
            value: v,
            label: t(`riskMaster.assessmentTypes.${v}`),
          }))}
          onChange={(v) => v && set({ assessmentType: v as typeof g.assessmentType })}
        />
      )}
      {ro ? (
        <Field
          label={k('general.vesselGroups')}
          value={g.vesselGroups.map((v) => t(`riskMaster.groups.${v}`)).join(', ')}
        />
      ) : (
        <MultiSelect
          label={k('general.vesselGroups')}
          value={g.vesselGroups}
          data={RA_VESSEL_GROUPS.map((v) => ({ value: v, label: t(`riskMaster.groups.${v}`) }))}
          onChange={(list) => {
            // ALL tek başına seçilir (ADR 0086 §2)
            const next = list.at(-1) === 'ALL' ? ['ALL'] : list.filter((v) => v !== 'ALL');
            if (next.length > 0) set({ vesselGroups: next as typeof g.vesselGroups });
          }}
        />
      )}
      {text('location', 300)}
      {text('team', 500)}
      {ro ? (
        <Field label={k('general.preparedDay')} value={g.preparedDay} />
      ) : (
        <DayInput
          label={k('general.preparedDay')}
          value={g.preparedDay}
          onChange={(v) => set({ preparedDay: v })}
        />
      )}
      {ro ? (
        <Field label={k('general.reviewDay')} value={g.reviewDay} />
      ) : (
        <DayInput
          label={k('general.reviewDay')}
          value={g.reviewDay}
          onChange={(v) => set({ reviewDay: v })}
        />
      )}
      <div className={classes.span3}>{area('procedures', 1000)}</div>
      <div className={classes.span3}>{area('scope', 2000)}</div>
      <div className={classes.span3}>{area('assumptions', 2000)}</div>
      <div className={classes.span3}>{area('exclusions', 2000)}</div>
      <div className={classes.span3}>
        <ChecklistCard {...props} />
      </div>
    </div>
  );
}

/**
 * Faaliyete özel operasyon maddeleri (K5; ADR 0089 §2; sahip kararı 2026-10-19, soru 4: isteğe
 * bağlı). Gemideki doğrulamanın checklist'i altı ortak madde, bu maddeler ve "mandatory" işaretli
 * mevcut kontrollerden kurulur. N/A yalnız işaretli maddede, gerekçeyle (SS §5, BR-04).
 */
function ChecklistCard(props: RaBuilderProps) {
  const { k } = useLabels();
  const checks = props.content.checks ?? [];
  const ro = !props.editable;
  const set = (next: RaLibraryCheck[]) =>
    props.onChange?.({ ...props.content, checks: next.length > 0 ? next : [] });
  const mandatory = props.content.rows.reduce(
    (n, r) => n + r.existingControls.filter((c) => c.requirement === 'mandatory').length,
    0,
  );
  return (
    <section className={classes.card} aria-label={k('checks.title')}>
      <div className={classes.cardHead}>
        <span>{k('checks.title')}</span>
        {ro || checks.length >= RA_LIBRARY_MAX_CHECKS ? null : (
          <Button
            size="compact-sm"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={() => set([...checks, blankCheck()])}
          >
            {k('checks.add')}
          </Button>
        )}
      </div>
      <div className={classes.cardBody}>
        <p className={classes.muted} style={{ margin: 0, fontSize: '0.8125rem' }}>
          {k('checks.note', { controls: mandatory })}
        </p>
        {checks.length === 0 ? (
          <span className={classes.muted}>{k('checks.empty')}</span>
        ) : ro ? (
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            {checks.map((c) => (
              <li key={c.id}>
                {c.text}
                {c.naAllowed ? (
                  <span className={classes.muted}> · {k('checks.naAllowed')}</span>
                ) : null}
              </li>
            ))}
          </ol>
        ) : (
          checks.map((c, i) => (
            <div key={c.id} className={classes.checkRow}>
              <TextInput
                label={k('checks.text', { n: i + 1 })}
                required
                value={c.text}
                maxLength={300}
                onChange={(e) =>
                  set(
                    checks.map((x) => (x.id === c.id ? { ...x, text: e.currentTarget.value } : x)),
                  )
                }
              />
              <Checkbox
                label={k('checks.naAllowed')}
                checked={c.naAllowed}
                onChange={(e) =>
                  set(
                    checks.map((x) =>
                      x.id === c.id ? { ...x, naAllowed: e.currentTarget.checked } : x,
                    ),
                  )
                }
              />
              <ActionIcon
                variant="subtle"
                color="red"
                aria-label={k('checks.remove', { n: i + 1 })}
                onClick={() => set(checks.filter((x) => x.id !== c.id))}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------
// Sekme 2: satırlar

function RowsTab(props: RaBuilderProps) {
  const { k } = useLabels();
  const [link, setLink] = useState<string | null>(null);
  const rows = props.content.rows;
  const ro = !props.editable;
  const set = (next: RaLibraryRow[]) => props.onChange?.({ ...props.content, rows: next });
  const lastStep = rows.at(-1)?.step ?? props.content.general.scope ?? '';
  return (
    <>
      <p className={classes.note}>{k('rows.note')}</p>
      {ro ? null : (
        <div className={classes.toolbar}>
          <Select
            label={k('rows.link')}
            placeholder={k('rows.linkPlaceholder')}
            searchable
            clearable
            value={link}
            data={(props.hazards ?? []).map((h) => ({
              value: h.id,
              label: `${h.code} · ${h.name}`,
            }))}
            onChange={setLink}
          />
          <Button
            variant="light"
            leftSection={<IconLink size={16} />}
            disabled={!link}
            onClick={() => {
              const hazard = props.hazards?.find((h) => h.id === link);
              if (!hazard) return;
              set([...rows, { ...blankRow(lastStep), hazardId: hazard.id, hazard: hazard.name }]);
              setLink(null);
            }}
          >
            {k('rows.link')}
          </Button>
          <Button
            variant="default"
            leftSection={<IconQuestionMark size={16} />}
            onClick={() => set([...rows, { ...blankRow(lastStep), hazardRequested: true }])}
          >
            {k('rows.request')}
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => set([...rows, blankRow(lastStep)])}
          >
            {k('rows.add')}
          </Button>
        </div>
      )}
      {rows.length === 0 ? <EmptyState title={k('rows.empty')} /> : null}
      {rows.map((row, index) => {
        const n = index + 1;
        return (
          <section key={row.id} className={classes.card} aria-label={rowTitle(k, row, n)}>
            <div className={classes.cardHead}>
              <span>{rowTitle(k, row, n)}</span>
              <span className={classes.scores}>
                {row.hazardId ? (
                  <StatusBadge tone="info">{k('rows.library')}</StatusBadge>
                ) : row.hazardRequested ? (
                  <StatusBadge tone="warning">{k('rows.requested')}</StatusBadge>
                ) : (
                  <StatusBadge tone="neutral">{k('rows.free')}</StatusBadge>
                )}
                {ro ? null : (
                  <span className={classes.rowActions}>
                    <ActionIcon
                      variant="subtle"
                      aria-label={k('rows.duplicate', { n })}
                      onClick={() =>
                        set([...rows.slice(0, n), duplicateRow(row), ...rows.slice(n)])
                      }
                    >
                      <IconCopy size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label={k('rows.remove', { n })}
                      onClick={() => set(rows.filter((r) => r.id !== row.id))}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </span>
                )}
              </span>
            </div>
            <div className={classes.cardGrid}>
              {ro ? (
                <>
                  <Field label={k('rows.step')} value={row.step} />
                  <Field label={k('rows.hazard')} value={row.hazard} />
                  <Field label={k('rows.consequence')} value={row.consequence} />
                  <Field label={k('rows.exposed')} value={row.exposed} />
                </>
              ) : (
                <>
                  <TextInput
                    label={`${k('rows.step')} ${n}`}
                    value={row.step}
                    maxLength={300}
                    required
                    onChange={(e) => setRow(props, index, { step: e.currentTarget.value })}
                  />
                  <TextInput
                    label={`${k('rows.hazard')} ${n}`}
                    value={row.hazard}
                    maxLength={300}
                    required
                    readOnly={row.hazardId !== null}
                    onChange={(e) => setRow(props, index, { hazard: e.currentTarget.value })}
                  />
                  <TextInput
                    label={`${k('rows.consequence')} ${n}`}
                    value={row.consequence ?? ''}
                    maxLength={1000}
                    onChange={(e) =>
                      setRow(props, index, { consequence: textOrNull(e.currentTarget.value) })
                    }
                  />
                  <TextInput
                    label={`${k('rows.exposed')} ${n}`}
                    value={row.exposed ?? ''}
                    maxLength={300}
                    onChange={(e) =>
                      setRow(props, index, { exposed: textOrNull(e.currentTarget.value) })
                    }
                  />
                </>
              )}
              {row.hazardRequested ? (
                <p className={`${classes.note} ${classes.span3}`}>{k('rows.requestNote')}</p>
              ) : null}
            </div>
          </section>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Sekme 3: Initial Risk

function ScaleSelect({
  label,
  value,
  readOnly,
  onPick,
}: {
  label: string;
  value: number;
  readOnly: boolean;
  onPick: (value: number) => void;
}) {
  return readOnly ? (
    <Field label={label} value={value} />
  ) : (
    <Select
      label={label}
      data={SCALE}
      value={String(value)}
      allowDeselect={false}
      onChange={(v) => v && onPick(Number(v))}
    />
  );
}

function InitialTab(props: RaBuilderProps) {
  const { k } = useLabels();
  const ro = !props.editable;
  return (
    <>
      <p className={classes.note}>{k('initial.note')}</p>
      {props.content.rows.map((row, index) => {
        const n = index + 1;
        const r = raRowRatings(row, props.matrix);
        const high =
          r.inherent.level === 'high' ||
          r.inherent.level === 'very_high' ||
          r.inherent.level === 'critical';
        return (
          <section key={row.id} className={classes.card} aria-label={rowTitle(k, row, n)}>
            <div className={classes.cardHead}>
              <span>{rowTitle(k, row, n)}</span>
              <span className={classes.scores}>
                <LevelBadge level={r.inherent.level} score={r.inherent.score} />
                {high ? <span className={classes.muted}>{k('initial.highNote')}</span> : null}
              </span>
            </div>
            <div className={classes.cardGrid}>
              <ScaleSelect
                label={`${k('initial.likelihood')} ${n}`}
                value={row.initial.likelihood}
                readOnly={ro}
                onPick={(v) => setRow(props, index, { initial: { ...row.initial, likelihood: v } })}
              />
              <ScaleSelect
                label={`${k('initial.severity')} ${n}`}
                value={row.initial.severity}
                readOnly={ro}
                onPick={(v) => setRow(props, index, { initial: { ...row.initial, severity: v } })}
              />
              <Field label={k('initial.score')} value={r.inherent.score} />
              {ro ? (
                <>
                  <Field label={k('initial.rationale')} value={row.initial.rationale} />
                  <Field label={k('initial.assumptions')} value={row.initial.assumptions} />
                </>
              ) : (
                <>
                  <Textarea
                    className={classes.span2}
                    label={`${k('initial.rationale')} ${n}`}
                    value={row.initial.rationale ?? ''}
                    maxLength={1000}
                    autosize
                    minRows={1}
                    onChange={(e) =>
                      setRow(props, index, {
                        initial: { ...row.initial, rationale: textOrNull(e.currentTarget.value) },
                      })
                    }
                  />
                  <Textarea
                    label={`${k('initial.assumptions')} ${n}`}
                    value={row.initial.assumptions ?? ''}
                    maxLength={1000}
                    autosize
                    minRows={1}
                    onChange={(e) =>
                      setRow(props, index, {
                        initial: { ...row.initial, assumptions: textOrNull(e.currentTarget.value) },
                      })
                    }
                  />
                </>
              )}
            </div>
          </section>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Sekme 4: Existing Controls

function ExistingTab(props: RaBuilderProps) {
  const { t, k, hierarchy } = useLabels();
  const ro = !props.editable;
  return (
    <>
      <p className={classes.note}>{k('existing.note')}</p>
      {props.content.rows.map((row, index) => {
        const n = index + 1;
        const r = raRowRatings(row, props.matrix);
        const setControls = (list: RaExistingControl[]) =>
          setRow(props, index, { existingControls: list });
        const setControl = (ci: number, patch: Partial<RaExistingControl>) =>
          setControls(row.existingControls.map((c, i) => (i === ci ? { ...c, ...patch } : c)));
        return (
          <section key={row.id} className={classes.card} aria-label={rowTitle(k, row, n)}>
            <div className={classes.cardHead}>
              <span>{rowTitle(k, row, n)}</span>
              <span className={classes.scores}>
                <LevelBadge level={r.inherent.level} score={r.inherent.score} />
                {r.current ? (
                  <>
                    <span className={classes.muted}>{k('existing.currentScore')}</span>
                    <LevelBadge level={r.current.level} score={r.current.score} />
                  </>
                ) : null}
              </span>
            </div>
            <div className={classes.cardBody}>
              {row.existingControls.length === 0 ? (
                <p className={classes.muted}>{k('existing.none')}</p>
              ) : null}
              {row.existingControls.map((c, ci) => {
                const cn = `${n}.${ci + 1}`;
                return ro ? (
                  <div key={c.id} className={classes.control}>
                    <Field label={k('existing.measure')} value={c.measure} />
                    <Field
                      label={k('existing.hierarchy')}
                      value={c.hierarchy.map((h) => t(`riskMaster.hierarchy.${h}`)).join(' / ')}
                    />
                    <Field label={k('existing.owner')} value={c.owner} />
                    <Field label={k('existing.inPlace')} value={k(`inPlace.${c.inPlace}`)} />
                    <span />
                  </div>
                ) : (
                  <div key={c.id} className={classes.control}>
                    <TextInput
                      className={classes.controlWide}
                      label={`${k('existing.measure')} ${cn}`}
                      required
                      value={c.measure}
                      maxLength={500}
                      onChange={(e) => setControl(ci, { measure: e.currentTarget.value })}
                    />
                    <MultiSelect
                      label={`${k('existing.hierarchy')} ${cn}`}
                      data={hierarchy}
                      value={c.hierarchy}
                      onChange={(v) => setControl(ci, { hierarchy: v as typeof c.hierarchy })}
                    />
                    <Select
                      label={`${k('existing.inPlace')} ${cn}`}
                      allowDeselect={false}
                      value={c.inPlace}
                      data={RA_IN_PLACE.map((v) => ({ value: v, label: k(`inPlace.${v}`) }))}
                      onChange={(v) => v && setControl(ci, { inPlace: v as typeof c.inPlace })}
                    />
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label={k('existing.remove', { n: cn })}
                      onClick={() => setControls(row.existingControls.filter((x) => x.id !== c.id))}
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                    <Select
                      label={`${k('existing.requirement')} ${cn}`}
                      clearable
                      value={c.requirement}
                      data={RA_CONTROL_REQUIREMENTS.map((v) => ({
                        value: v,
                        label: t(`riskMaster.requirements.${v}`),
                      }))}
                      onChange={(v) =>
                        setControl(ci, { requirement: (v ?? null) as typeof c.requirement })
                      }
                    />
                    <TextInput
                      label={`${k('existing.owner')} ${cn}`}
                      value={c.owner ?? ''}
                      maxLength={120}
                      onChange={(e) => setControl(ci, { owner: textOrNull(e.currentTarget.value) })}
                    />
                    <TextInput
                      label={`${k('existing.procedure')} ${cn}`}
                      value={c.procedure ?? ''}
                      maxLength={300}
                      onChange={(e) =>
                        setControl(ci, { procedure: textOrNull(e.currentTarget.value) })
                      }
                    />
                    <TextInput
                      label={`${k('existing.evidence')} ${cn}`}
                      value={c.evidence ?? ''}
                      maxLength={500}
                      onChange={(e) =>
                        setControl(ci, { evidence: textOrNull(e.currentTarget.value) })
                      }
                    />
                    <span />
                    <TextInput
                      className={classes.controlWide}
                      label={`${k('existing.effectiveness')} ${cn}`}
                      value={c.effectiveness ?? ''}
                      maxLength={500}
                      onChange={(e) =>
                        setControl(ci, { effectiveness: textOrNull(e.currentTarget.value) })
                      }
                    />
                  </div>
                );
              })}
              {ro ? null : (
                <div className={classes.toolbar}>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => setControls([...row.existingControls, blankExisting()])}
                  >
                    {k('existing.add')}
                  </Button>
                  <Checkbox
                    label={k('existing.current')}
                    checked={row.current !== null}
                    onChange={(e) =>
                      setRow(props, index, {
                        current: e.currentTarget.checked
                          ? { likelihood: row.initial.likelihood, severity: row.initial.severity }
                          : null,
                      })
                    }
                  />
                </div>
              )}
              {row.current && !ro ? (
                <div className={classes.grid}>
                  <ScaleSelect
                    label={`${k('existing.currentScore')} · ${k('initial.likelihood')} ${n}`}
                    value={row.current.likelihood}
                    readOnly={false}
                    onPick={(v) =>
                      setRow(props, index, {
                        current: { severity: row.current?.severity ?? 1, likelihood: v },
                      })
                    }
                  />
                  <ScaleSelect
                    label={`${k('existing.currentScore')} · ${k('initial.severity')} ${n}`}
                    value={row.current.severity}
                    readOnly={false}
                    onPick={(v) =>
                      setRow(props, index, {
                        current: { likelihood: row.current?.likelihood ?? 1, severity: v },
                      })
                    }
                  />
                </div>
              ) : null}
            </div>
          </section>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Sekme 5: Additional Controls

function AdditionalTab(props: RaBuilderProps) {
  const { k, hierarchy } = useLabels();
  const ro = !props.editable;
  return (
    <>
      <p className={classes.note}>{k('additional.note')}</p>
      {props.content.rows.map((row, index) => {
        const n = index + 1;
        const r = raRowRatings(row, props.matrix);
        const setControls = (list: RaAdditionalControl[]) =>
          setRow(props, index, { additionalControls: list });
        const setControl = (ci: number, patch: Partial<RaAdditionalControl>) =>
          setControls(row.additionalControls.map((c, i) => (i === ci ? { ...c, ...patch } : c)));
        const baseline = r.current ?? r.inherent;
        return (
          <section key={row.id} className={classes.card} aria-label={rowTitle(k, row, n)}>
            <div className={classes.cardHead}>
              <span>{rowTitle(k, row, n)}</span>
              <LevelBadge level={baseline.level} score={baseline.score} />
            </div>
            <div className={classes.cardBody}>
              {row.additionalControls.length === 0 ? (
                <p className={classes.muted}>{k('additional.none')}</p>
              ) : null}
              {row.additionalControls.map((c, ci) => {
                const cn = `${n}.${ci + 1}`;
                return ro ? (
                  <div key={c.id} className={classes.control}>
                    <Field label={k('additional.measure')} value={c.measure} />
                    <Field
                      label={k('additional.effect')}
                      value={c.expectedEffect ? k(`effects.${c.expectedEffect}`) : null}
                    />
                    <Field label={k('additional.responsible')} value={c.responsibleRole} />
                    <Field
                      label={k('additional.status')}
                      value={k(`additionalStatuses.${c.status}`)}
                    />
                    <span />
                  </div>
                ) : (
                  <div key={c.id} className={classes.control}>
                    <TextInput
                      className={classes.controlWide}
                      label={`${k('additional.measure')} ${cn}`}
                      required
                      value={c.measure}
                      maxLength={500}
                      onChange={(e) => setControl(ci, { measure: e.currentTarget.value })}
                    />
                    <MultiSelect
                      label={`${k('additional.hierarchy')} ${cn}`}
                      data={hierarchy}
                      value={c.hierarchy}
                      onChange={(v) => setControl(ci, { hierarchy: v as typeof c.hierarchy })}
                    />
                    <Select
                      label={`${k('additional.status')} ${cn}`}
                      allowDeselect={false}
                      value={c.status}
                      data={RA_ADDITIONAL_STATUSES.map((v) => ({
                        value: v,
                        label: k(`additionalStatuses.${v}`),
                      }))}
                      onChange={(v) => v && setControl(ci, { status: v as typeof c.status })}
                    />
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label={k('additional.remove', { n: cn })}
                      onClick={() =>
                        setControls(row.additionalControls.filter((x) => x.id !== c.id))
                      }
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                    <Select
                      label={`${k('additional.effect')} ${cn}`}
                      clearable
                      value={c.expectedEffect}
                      data={RA_EXPECTED_EFFECTS.map((v) => ({
                        value: v,
                        label: k(`effects.${v}`),
                      }))}
                      onChange={(v) =>
                        setControl(ci, { expectedEffect: (v ?? null) as typeof c.expectedEffect })
                      }
                    />
                    <TextInput
                      label={`${k('additional.responsible')} ${cn}`}
                      value={c.responsibleRole ?? ''}
                      maxLength={120}
                      onChange={(e) =>
                        setControl(ci, { responsibleRole: textOrNull(e.currentTarget.value) })
                      }
                    />
                    <DayInput
                      label={`${k('additional.due')} ${cn}`}
                      value={c.dueDay}
                      onChange={(v) => setControl(ci, { dueDay: v })}
                    />
                    <TextInput
                      label={`${k('additional.verifier')} ${cn}`}
                      value={c.verifier ?? ''}
                      maxLength={120}
                      onChange={(e) =>
                        setControl(ci, { verifier: textOrNull(e.currentTarget.value) })
                      }
                    />
                    <span />
                    <TextInput
                      className={classes.controlWide}
                      label={`${k('additional.effectRationale')} ${cn}`}
                      value={c.effectRationale ?? ''}
                      maxLength={500}
                      onChange={(e) =>
                        setControl(ci, { effectRationale: textOrNull(e.currentTarget.value) })
                      }
                    />
                    <TextInput
                      label={`${k('additional.evidence')} ${cn}`}
                      value={c.evidence ?? ''}
                      maxLength={500}
                      onChange={(e) =>
                        setControl(ci, { evidence: textOrNull(e.currentTarget.value) })
                      }
                    />
                    <TextInput
                      label={`${k('additional.criteria')} ${cn}`}
                      value={c.completionCriteria ?? ''}
                      maxLength={500}
                      onChange={(e) =>
                        setControl(ci, { completionCriteria: textOrNull(e.currentTarget.value) })
                      }
                    />
                  </div>
                );
              })}
              {ro ? null : (
                <div className={classes.toolbar}>
                  <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => setControls([...row.additionalControls, blankAdditional()])}
                  >
                    {k('additional.add')}
                  </Button>
                </div>
              )}
            </div>
          </section>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Sekme 6: Residual

function ResidualTab(props: RaBuilderProps) {
  const { t, k } = useLabels();
  const ro = !props.editable;
  return (
    <>
      <p className={classes.note}>{k('residual.note')}</p>
      {props.content.rows.map((row, index) => {
        const n = index + 1;
        const r = raRowRatings(row, props.matrix);
        const residual = row.residual;
        const setResidual = (patch: Partial<NonNullable<RaLibraryRow['residual']>>) =>
          setRow(props, index, {
            residual: {
              likelihood: residual?.likelihood ?? row.initial.likelihood,
              severity: residual?.severity ?? row.initial.severity,
              rationale: residual?.rationale ?? null,
              dependency: residual?.dependency ?? null,
              ...patch,
            },
          });
        const scale = (field: 'likelihood' | 'severity') =>
          ro ? (
            <Field
              label={k(`residual.${field}`)}
              value={residual ? residual[field] : k('residual.notSet')}
            />
          ) : (
            <Select
              label={`${k(`residual.${field}`)} ${n}`}
              placeholder={k('residual.notSet')}
              data={SCALE}
              value={residual ? String(residual[field]) : null}
              clearable
              onChange={(v) =>
                v === null
                  ? setRow(props, index, { residual: null })
                  : setResidual({ [field]: Number(v) })
              }
            />
          );
        return (
          <section key={row.id} className={classes.card} aria-label={rowTitle(k, row, n)}>
            <div className={classes.cardHead}>
              <span>{rowTitle(k, row, n)}</span>
              <span className={classes.scores}>
                <span className={classes.muted}>{k('residual.scores')}</span>
                <LevelBadge level={r.inherent.level} score={r.inherent.score} />
                {r.current ? <LevelBadge level={r.current.level} score={r.current.score} /> : '—'}
                {r.residual ? (
                  <LevelBadge level={r.residual.level} score={r.residual.score} />
                ) : (
                  '—'
                )}
              </span>
            </div>
            <div className={classes.cardGrid}>
              {scale('likelihood')}
              {scale('severity')}
              <Field
                label={k('residual.acceptance')}
                value={
                  <span className={classes.scores}>
                    <AcceptanceBadge acceptance={r.acceptance} />
                    {r.residual ? (
                      <span className={classes.muted}>
                        {r.residualVerified ? k('residual.verified') : k('residual.estimate')}
                      </span>
                    ) : null}
                    {r.acceptance === 'escalation' && r.acceptedBy.length > 0 ? (
                      <span className={classes.muted}>
                        {k('residual.acceptedBy', {
                          roles: r.acceptedBy
                            .map((a) => t(`risk.matrixProfile.acceptors.${a}`))
                            .join(' + '),
                        })}
                      </span>
                    ) : null}
                  </span>
                }
              />
              {ro ? (
                <>
                  <Field label={k('residual.rationale')} value={residual?.rationale} />
                  <Field label={k('residual.dependency')} value={residual?.dependency} />
                </>
              ) : (
                <>
                  <Textarea
                    className={classes.span2}
                    label={`${k('residual.rationale')} ${n}`}
                    disabled={!residual}
                    value={residual?.rationale ?? ''}
                    maxLength={1000}
                    autosize
                    minRows={1}
                    onChange={(e) => setResidual({ rationale: textOrNull(e.currentTarget.value) })}
                  />
                  <Textarea
                    label={`${k('residual.dependency')} ${n}`}
                    disabled={!residual}
                    value={residual?.dependency ?? ''}
                    maxLength={1000}
                    autosize
                    minRows={1}
                    onChange={(e) => setResidual({ dependency: textOrNull(e.currentTarget.value) })}
                  />
                </>
              )}
            </div>
          </section>
        );
      })}
    </>
  );
}

export function AcceptanceBadge({ acceptance }: { acceptance: string | null }) {
  const { t } = useTranslation();
  if (!acceptance) return <span>—</span>;
  const tone =
    acceptance === 'acceptable'
      ? 'success'
      : acceptance === 'controls_required'
        ? 'warning'
        : 'danger';
  return <StatusBadge tone={tone}>{t(`risk.matrixProfile.acceptances.${acceptance}`)}</StatusBadge>;
}

/** Sorunun metni: satır numarasıyla. */
export function useIssueText(content: RaLibraryContent) {
  const { t } = useTranslation();
  return (issue: RaPublishIssue) =>
    t(`riskLibrary.issues.${issue.code}`, { n: issueRow(content, issue) ?? '' });
}
